import { opportunityFetch } from "./opportunityFetch";
import { resolveManagerPresetFormatKey } from "./formatRegistry";
import type { OpportunityFormatKey } from "@/types/opportunity";
import type { OpportunityDocumentArchetype } from "./documentArchetypes";
import {
  defaultAlternateForArchetype,
  type ProposalAlternate,
  type ProposalAlternatePage,
} from "./proposalAlternateTypes";
import {
  pickProposalAlternate,
  proposalAlternateManifestIssue,
} from "./proposalAlternateApply";
import { OPPORTUNITY_PROPOSAL_ALTERNATES_RENDER_ENABLED } from "./proposalAlternateFlags";

export type ListProposalAlternatesFilters = {
  format_key?: OpportunityFormatKey;
  product_type?: string;
  archetype?: OpportunityDocumentArchetype;
};

export function listProposalAlternates(filters: ListProposalAlternatesFilters = {}) {
  const q = new URLSearchParams();
  if (filters.format_key) q.set("format_key", filters.format_key);
  if (filters.product_type) q.set("product_type", filters.product_type);
  if (filters.archetype) q.set("archetype", filters.archetype);
  const qs = q.toString();
  const path = qs
    ? `/api/opportunity/proposal-alternate-list?${qs}`
    : "/api/opportunity/proposal-alternate-list";
  return opportunityFetch<{ ok: true; alternates: ProposalAlternate[]; doc_document_id_supported?: boolean }>(path, {
    method: "GET",
  });
}

export type UpsertProposalAlternatePayload = {
  format_key: OpportunityFormatKey;
  product_type: string;
  alternate_key: string;
  label: string;
  description?: string | null;
  sort_order?: number;
  is_default?: boolean;
  /** Defaults to 'proposal' server-side. */
  archetype?: OpportunityDocumentArchetype;
  pages: ProposalAlternatePage[];
};

export function upsertProposalAlternate(payload: UpsertProposalAlternatePayload) {
  return opportunityFetch<{ ok: true; alternate: ProposalAlternate }>(
    "/api/opportunity/proposal-alternate-upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteProposalAlternate(args: {
  format_key: OpportunityFormatKey;
  product_type: string;
  alternate_key: string;
}) {
  return opportunityFetch<{ ok: true }>("/api/opportunity/proposal-alternate-delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
}

/** Load alternates for sales render; synthesize default when none configured. */
export async function resolveProposalAlternatesForProduct(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
  archetype?: OpportunityDocumentArchetype;
}): Promise<ProposalAlternate[]> {
  const archetype = args.archetype ?? "proposal";
  const r = await listProposalAlternates({
    format_key: args.formatKey,
    product_type: args.productType,
    archetype,
  });
  // Client-side re-filter: pre-archetype databases return all rows as
  // proposals regardless of the query filter.
  const rows = r.ok
    ? r.data.alternates.filter((a) => (a.archetype ?? "proposal") === archetype)
    : [];
  if (rows.length > 0) return rows;
  return [
    defaultAlternateForArchetype({
      formatKey: args.formatKey,
      productType: args.productType,
      archetype,
    }),
  ];
}

/**
 * Pure selection for tests and render path. Only an explicit, matching,
 * manifest-complete alternate key selects an alternate; everything else
 * (no key, unknown key, incomplete manifest, kill-switch) renders the
 * synthetic Primary manifest.
 */
export function resolveProposalAlternateSelection(args: {
  alternates: readonly ProposalAlternate[];
  alternateKey?: string | null;
  formatKey: OpportunityFormatKey;
  productType: string;
  archetype?: OpportunityDocumentArchetype;
}): ProposalAlternate {
  const standard = defaultAlternateForArchetype({
    formatKey: args.formatKey,
    productType: args.productType,
    archetype: args.archetype ?? "proposal",
  });
  if (!OPPORTUNITY_PROPOSAL_ALTERNATES_RENDER_ENABLED) return standard;
  const picked = pickProposalAlternate(args.alternates, args.alternateKey);
  if (!picked) return standard;
  if (proposalAlternateManifestIssue(picked.pages)) return standard;
  return picked;
}

export async function resolveSelectedProposalAlternate(args: {
  productType: string;
  alternateKey?: string | null;
  archetype?: OpportunityDocumentArchetype;
}): Promise<{ alternates: ProposalAlternate[]; selected: ProposalAlternate }> {
  const formatKey = resolveManagerPresetFormatKey(args.productType);
  const alternates = await resolveProposalAlternatesForProduct({
    formatKey,
    productType: args.productType,
    archetype: args.archetype,
  });
  const selected = resolveProposalAlternateSelection({
    alternates,
    alternateKey: args.alternateKey,
    formatKey,
    productType: args.productType,
    archetype: args.archetype,
  });
  return { alternates, selected };
}

/**
 * DB alternates a sales rep may pick, excluding the synthetic Primary
 * and any manifest-incomplete rows (those would silently fall back to
 * Primary at render, so offering them is misleading).
 */
export function salesAlternateChoices(alternates: readonly ProposalAlternate[]): ProposalAlternate[] {
  const fromDb = alternates.filter(
    (a) => a.id !== "default" && !proposalAlternateManifestIssue(a.pages),
  );
  return [...fromDb].sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
}
